import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Badge } from './badge';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const RecalculatePricingModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCount = 0,
  visibleCount = 0,
  totalCount = 0,
  isLoading = false,
  userRoles = ['viewer']
}) => {
  const [updateManufacturingCosts, setUpdateManufacturingCosts] = useState(false);
  const [resetManualOverrides, setResetManualOverrides] = useState(false);
  const [recalculateScope, setRecalculateScope] = useState('selected'); // 'selected', 'visible', 'all'

  // Determine calculation scope based on selection
  React.useEffect(() => {
    if (selectedCount > 0) {
      setRecalculateScope('selected');
    } else {
      setRecalculateScope('visible');
    }
  }, [selectedCount]);

  // RBAC permissions
  const hasFinanceAccess = userRoles.includes('finance') || userRoles.includes('owner');
  const canResetOverrides = hasFinanceAccess; // Only Finance and Owner can reset manual overrides

  const getProductCount = () => {
    switch (recalculateScope) {
      case 'selected':
        return selectedCount;
      case 'visible':
        return visibleCount;
      case 'all':
        return totalCount;
      default:
        return 0;
    }
  };

  const getDescription = () => {
    const count = getProductCount();
    switch (recalculateScope) {
      case 'selected':
        return `${count} selected product${count !== 1 ? 's' : ''}`;
      case 'visible':
        return `all visible products (${count} items)`;
      case 'all':
        return `ALL products in database (${count} SKUs)`;
      default:
        return '';
    }
  };

  const handleConfirm = () => {
    onConfirm({
      scope: recalculateScope,
      updateManufacturingCosts,
      resetManualOverrides,
      count: getProductCount()
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] dark:bg-gray-800 dark:text-gray-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Recalculate Pricing
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Main Description */}
          <div className="space-y-2">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Recalculate pricing for <strong>{getDescription()}</strong>?
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This will update System Price based on current costs and target margins.
            </p>
          </div>

          {/* Scope Selection */}
          <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Products to recalculate:
            </div>
            
            {selectedCount > 0 && (
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="scope-selected"
                  name="scope"
                  value="selected"
                  checked={recalculateScope === 'selected'}
                  onChange={(e) => setRecalculateScope(e.target.value)}
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                />
                <label htmlFor="scope-selected" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  {selectedCount} selected products
                </label>
                <Badge variant="secondary" className="text-xs">
                  Selected
                </Badge>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="scope-visible"
                name="scope"
                value="visible"
                checked={recalculateScope === 'visible'}
                onChange={(e) => setRecalculateScope(e.target.value)}
                className="w-4 h-4 text-blue-600 dark:text-blue-400"
              />
              <label htmlFor="scope-visible" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                All visible products ({visibleCount} items)
              </label>
              <Badge variant="outline" className="text-xs">
                Filtered View
              </Badge>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="scope-all"
                name="scope"
                value="all"
                checked={recalculateScope === 'all'}
                onChange={(e) => setRecalculateScope(e.target.value)}
                className="w-4 h-4 text-blue-600 dark:text-blue-400"
              />
              <label htmlFor="scope-all" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                All products in database ({totalCount} SKUs)
              </label>
              <Badge variant="destructive" className="text-xs">
                Full Database
              </Badge>
            </div>

            {recalculateScope === 'all' && (
              <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                <span className="text-xs text-yellow-800 dark:text-yellow-200">
                  This may take a few moments
                </span>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Options:
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="update-manufacturing"
                checked={updateManufacturingCosts}
                onCheckedChange={setUpdateManufacturingCosts}
              />
              <label
                htmlFor="update-manufacturing"
                className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                Update manufacturing costs from latest data
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="reset-overrides"
                checked={resetManualOverrides}
                onCheckedChange={setResetManualOverrides}
                disabled={!canResetOverrides}
              />
              <label
                htmlFor="reset-overrides"
                className={`text-sm cursor-pointer ${
                  canResetOverrides 
                    ? 'text-gray-700 dark:text-gray-300' 
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                Reset manual overrides to system prices
                {!canResetOverrides && (
                  <span className="ml-2 text-xs text-gray-400">(Finance access required)</span>
                )}
              </label>
            </div>
          </div>

          {/* Note */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> Manual price overrides will be preserved unless you check "Reset manual overrides"
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Recalculating...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Recalculate
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecalculatePricingModal;